import { Component, computed, inject, signal, viewChild } from '@angular/core';
import { LocalizationPipe, LocalizationService } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnPill,
  ZnSelectQueryParams,
  ZnSingleSelect,
} from '@zendra/shared';
import type { StandPartitionStandDetailDto } from 'orchestrator';
import { firstValueFrom } from 'rxjs';

import type { IStepSelectStandToDivideData } from '../interfaces/IStepSelectStandToDivideData';
import { StandPartitionPlanningService } from '../services/stand-partition-planning.service';

type SelectValue = string | string[] | null;

interface StandDetailsViewModel {
  establishment: string;
  standArea: string;
  standType: string;
  cycle: string;
  rotation: string;
  forestSituation: string;
  nearbyPopulation: string;
  plantationDate: string;
  plantationType: string;
  geneticMaterial: string;
  species: string;
  spacing: string;
  ownPlantation: string;
  landContract: string;
  forestContract: string;
  propertyType: string;
}

const EMPTY_VALUE = '-----';

@Component({
  selector: 'lib-step-select-stand-to-divide',
  standalone: true,
  imports: [LocalizationPipe, ZnSingleSelect, ZnPill],
  templateUrl: './step-select-stand-to-divide.html',
  styleUrl: './step-select-stand-to-divide.scss',
})
export class StepSelectStandToDivide
  implements WizardStepComponent<IStepSelectStandToDivideData>
{
  readonly standSelect = viewChild('standSelect', { read: ZnSingleSelect });

  private readonly toaster = inject(ToasterService);
  private readonly localizationService = inject(LocalizationService);
  private readonly standPartitionService = inject(StandPartitionPlanningService);

  readonly localizationKeyPrefix = 'Geographic::StandDivision.StepSelectStandToDivide';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly loading = signal(false);
  readonly selectedStandId = signal<string | null>(null);
  readonly selectedStandDetail = signal<StandPartitionStandDetailDto | null>(null);

  readonly standOptions = computed(() => {
    const selectedStand = this.selectedStandDetail();
    if (selectedStand?.id === undefined) {
      return [];
    }

    const standId = String(selectedStand.id);
    return [{ key: standId, value: selectedStand.name ?? standId }];
  });

  readonly selectedStand = computed(() => this.selectedStandDetail()?.name ?? null);
  readonly selectedStandDetails = computed<StandDetailsViewModel | null>(() => {
    const detail = this.selectedStandDetail();
    if (!detail) {
      return null;
    }

    return {
      establishment: this.displayValue(detail.establishmentName),
      standArea: this.formatArea(detail.standArea),
      standType: this.displayValue(detail.standTypeName),
      cycle: this.formatNumericValue(detail.cycleValue),
      rotation: this.formatNumericValue(detail.rotationValue),
      forestSituation: this.displayValue(detail.forestrySituationName),
      nearbyPopulation: this.formatBooleanValue(detail.hasNearbyPopulation),
      plantationDate: this.formatDate(detail.plantation?.plantationDate),
      plantationType: this.displayValue(detail.plantation?.plantationTypeName),
      geneticMaterial: this.displayValue(detail.plantation?.geneticMaterialName),
      species: this.displayValue(detail.plantation?.speciesName),
      spacing: this.displayValue(detail.plantation?.spacingName),
      ownPlantation: this.formatBooleanValue(detail.plantation?.isOwnPlantation),
      landContract: this.displayValue(detail.cycleRotation?.landContractNumber),
      forestContract: this.displayValue(detail.cycleRotation?.fomentContractNumber),
      propertyType: this.displayValue(detail.cycleRotation?.propertyTypeName),
    };
  });

  readonly standOptionsLoader = (params?: ZnSelectQueryParams) =>
    this.standPartitionService.searchStandOptions(params);

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const currentState =
      context.getStepState<IStepSelectStandToDivideData>(context.activeStepId())?.data;

    this.selectedStandId.set(
      currentState?.standId !== null && currentState?.standId !== undefined
        ? String(currentState.standId)
        : null,
    );
    this.selectedStandDetail.set(currentState?.standDetail ?? null);

    queueMicrotask(() => {
      this.standSelect()?.writeValue(this.selectedStandId());
    });
  }

  async onStandChange(value: SelectValue): Promise<void> {
    const standId = this.toSingleSelectValue(value);
    if (!standId) {
      this.clearSelection();
      return;
    }

    const numericStandId = Number(standId);
    if (!Number.isFinite(numericStandId)) {
      this.clearSelection();
      queueMicrotask(() => {
        this.standSelect()?.writeValue(null);
      });
      return;
    }

    this.loading.set(true);

    try {
      const selectedStand = await firstValueFrom(
        this.standPartitionService.getStandDetail(numericStandId),
      );

      this.selectedStandId.set(selectedStand ? String(selectedStand.id) : null);
      this.selectedStandDetail.set(selectedStand);

      if (!selectedStand) {
        queueMicrotask(() => {
          this.standSelect()?.writeValue(null);
        });
      }
    } catch {
      this.clearSelection();
      queueMicrotask(() => {
        this.standSelect()?.writeValue(null);
      });
    } finally {
      this.loading.set(false);
    }
  }

  async canLeave(): Promise<boolean> {
    const selectedStand = this.selectedStandDetail();
    if (selectedStand?.id === undefined) {
      this.toaster.error(
        `${ZendraLocalizationServices.Geographic}::StandDivision.StepSelectStandToDivide.SelectionRequired`,
        '',
        { life: DEFAULT_TOASTER_LIFE },
      );
      return false;
    }

    try {
      await firstValueFrom(this.standPartitionService.ensureCanAdvance(selectedStand.id));
      return true;
    } catch {
      return false;
    }
  }

  canLeaveSync(): boolean {
    return !this.loading();
  }

  hasMeaningfulData(): boolean {
    return !!this.selectedStandDetail();
  }

  collect(context: IWizardContext): IStepSelectStandToDivideData {
    const selectedStand = this.selectedStandDetail();
    const data: IStepSelectStandToDivideData = {
      standId: selectedStand?.id ?? null,
      standName: selectedStand?.name ?? null,
      standArea: selectedStand?.standArea ?? null,
      standDetail: selectedStand,
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: !!selectedStand,
      completed: true,
      data,
    });
    return data;
  }

  displayValue(value: string | null | undefined): string {
    return value?.trim() ? value : EMPTY_VALUE;
  }

  private clearSelection(): void {
    this.selectedStandId.set(null);
    this.selectedStandDetail.set(null);
  }

  private toSingleSelectValue(value: SelectValue): string | null {
    if (Array.isArray(value)) {
      return value[0] ?? null;
    }

    return value || null;
  }

  private formatArea(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
      return EMPTY_VALUE;
    }

    return value.toFixed(2).replace('.', ',');
  }

  private formatNumericValue(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return String(value);
  }

  private formatBooleanValue(value: boolean | null | undefined): string {
    if (value === null || value === undefined) {
      return EMPTY_VALUE;
    }

    return this.localizationService.instant(value ? 'AbpUi::Yes' : 'AbpUi::No');
  }

  private formatDate(value: string | null | undefined): string {
    if (!value?.trim()) {
      return EMPTY_VALUE;
    }

    const [year, month, day] = value.split('-');
    if (!year || !month || !day) {
      return value;
    }

    return `${day}/${month}/${year}`;
  }
}
